import React, { useState } from 'react';
import { MainNavItem } from '../../molecules/MainNavItem';
import { SubNavItem } from '../../molecules/SubNavItem';
import { MainNavMobileItem } from '../../molecules/MainNavMobileItem';
import { LayoutContainer } from '../../atoms/LayoutContainer';
import { Icon } from '../../atoms/Icon';
import { Link } from '../../atoms/Link';
import heartInternetLogo from '../../assets/images/heart-internet-logo.svg';
import heartInternetLogoMobile from '../../assets/images/heart-internet-logo-mobile.svg';
import './MainNav.css';

export interface SubNavItemData {
  label: string;
  href: string;
  description: string;
  icon?: string;
  badgeText?: string;
  badgeColor?: string;
}
export interface MainNavItemData {
  label: string;
  href: string;
  badgeText?: string;
  badgeColor?: string;
  subItems?: SubNavItemData[];
}

export interface MainNavProps {
  items: MainNavItemData[];
  isLoggedIn?: boolean;
  wwwDomain?: string;
  onAccountClick?: () => void;
  /**
   * Callback when the mobile menu is toggled (hamburger button)
   */
  onMenuToggle?: (isOpen: boolean) => void;
  /**
   * Whether the sidebar is open (for showing active state on Account icon)
   */
  sidebarOpen?: boolean;
  /**
   * Whether the mobile nav bar is fixed to the top of the viewport.
   * Defaults to true (customer behaviour). Set to false for sites where the
   * mobile nav should scroll with the page.
   */
  isMobileFixed?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const MainNav: React.FC<MainNavProps> = ({
  items,
  isLoggedIn = false,
  wwwDomain = 'www.heartinternet.uk',
  onAccountClick,
  onMenuToggle,
  sidebarOpen = false,
  isMobileFixed = true,
  className = '',
  style,
}) => {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const protocol = 'https';

  const handleMouseEnter = (label: string) => {
    setHoveredItem(label);
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  const handleAccountClick = () => {
    if (isLoggedIn && onAccountClick) {
      setMobileMenuOpen(false); // Close hamburger menu when opening sidebar
      onAccountClick();
    }
  };

  const handleMenuToggle = () => {
    const newState = !mobileMenuOpen;
    setMobileMenuOpen(newState);
    onMenuToggle?.(newState);
  };

  const currentSubItems = items.find((item) => item.label === hoveredItem)?.subItems;

  return (
    <div
      id="hi-ui-mainnav"
      className={`hi-ui-mainnav-wrapper${!isMobileFixed ? ' hi-ui-mainnav-wrapper--mobile-scroll' : ''}`}
    >
      {/* Desktop Navigation */}
      <nav
        className={`hi-ui hi-ui-mainnav ${className}`.trim()}
        style={style}
        onMouseLeave={handleMouseLeave}
      >
        <LayoutContainer>
          <div className="hi-ui-mainnav__content">
            <a href={`${protocol}://${wwwDomain}`} className="hi-ui-mainnav__logo">
              <img src={heartInternetLogo} alt="Heart Internet" />
            </a>
            <div className="hi-ui-mainnav__items">
              {items.map((item) => (
                <MainNavItem
                  key={item.label}
                  label={item.label}
                  href={`${protocol}://${wwwDomain}${item.href}`}
                  badgeText={item.badgeText}
                  badgeColor={item.badgeColor}
                  isActive={hoveredItem === item.label}
                  onMouseEnter={() => handleMouseEnter(item.label)}
                />
              ))}
            </div>
          </div>
        </LayoutContainer>
        {currentSubItems && currentSubItems.length > 0 && (
          <div className="hi-ui-mainnav__dropdown">
            <LayoutContainer>
              <div className="hi-ui-mainnav__subnav">
                {currentSubItems.map((subItem) => (
                  <SubNavItem
                    key={subItem.label}
                    label={subItem.label}
                    href={`${protocol}://${wwwDomain}${subItem.href}`}
                    description={subItem.description}
                    icon={subItem.icon}
                    badgeText={subItem.badgeText}
                    badgeColor={subItem.badgeColor}
                  />
                ))}
              </div>
            </LayoutContainer>
          </div>
        )}
      </nav>

      {/* Mobile Navigation Bar */}
      <nav className="hi-ui hi-ui-mainnav-mobile">
        <div className="hi-ui-mainnav-mobile__group">
          <MainNavMobileItem
            icon={<img src={heartInternetLogoMobile} alt="Heart Internet" />}
            label="Home"
            href={`${protocol}://${wwwDomain}`}
          />
          <MainNavMobileItem
            icon={<Icon name="search" size={36} />}
            label="Search"
            href={`${protocol}://${wwwDomain}/domain-names`}
          />
        </div>
        <div className="hi-ui-mainnav-mobile__group">
          <MainNavMobileItem
            icon={<Icon name="user" size={36} />}
            label="Account"
            href={isLoggedIn ? undefined : '/manage/login.cgi'}
            onClick={isLoggedIn ? handleAccountClick : undefined}
            active={sidebarOpen}
          />
          <MainNavMobileItem
            icon={<Icon name="support" size={36} />}
            label="Support"
            href={`${protocol}://${wwwDomain}/support/`}
          />
          <MainNavMobileItem
            icon={<Icon name="hamburger" size={36} />}
            label="Menu"
            onClick={handleMenuToggle}
            active={mobileMenuOpen}
          />
        </div>
      </nav>

      {/* Mobile Menu Panel */}
      <div
        className={`hi-ui-mainnav-mobile-menu ${mobileMenuOpen ? 'hi-ui-mainnav-mobile-menu--open' : ''}`}
      >
        <div className="hi-ui-mainnav-mobile-menu__content">
          {items.map((item) => (
            <Link
              key={item.label}
              href={`${protocol}://${wwwDomain}${item.href}`}
              noUnderline
              className="hi-ui-mainnav-mobile-menu__item"
            >
              {item.label}
              {item.badgeText && (
                <span className="hi-ui-mainnav-mobile-menu__sale-badge">
                  <span className="hi-ui-nav-badge" style={{ backgroundColor: item.badgeColor }}>
                    {item.badgeText}
                  </span>
                </span>
              )}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};
